import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Radar, AlertCircle, Clock } from 'lucide-react';

export function OIDCCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithOIDC } = useAuth();
  const [error, setError] = useState('');

  const code = searchParams.get('code');
  const status = searchParams.get('status');
  const message = searchParams.get('message');

  useEffect(() => {
    if (status === 'pending') return;
    if (status === 'error') {
      setError(message || 'Authentication failed');
      return;
    }
    if (!code) {
      setError('Missing authentication code');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await loginWithOIDC(code);
        if (!cancelled) navigate('/', { replace: true });
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to complete sign-in');
      }
    })();
    return () => { cancelled = true; };
  }, [code, status, message, loginWithOIDC, navigate]);

  // Pending approval view
  if (status === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm text-center">
          <div className="flex flex-col items-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg mb-4">
              <Clock className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl font-bold text-foreground">Pending Approval</h1>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            Your account has been created and is awaiting administrator approval.
            You'll be able to sign in once an admin approves your access.
          </p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // Error view
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm text-center">
          <div className="flex flex-col items-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center shadow-lg mb-4">
              <AlertCircle className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl font-bold text-foreground">Sign-in Failed</h1>
          </div>
          <p className="text-sm text-muted-foreground mb-6">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // Loading state (exchanging code for tokens)
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm text-center">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sabio-purple to-indigo-600 flex items-center justify-center shadow-lg shadow-sabio-purple/20 mb-4">
            <Radar className="w-7 h-7 text-white animate-pulse" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Completing sign-in...</h1>
        </div>
        <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
      </div>
    </div>
  );
}
